import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent } from "../ui/dialog";
import { SubmitHandler, useForm } from "react-hook-form";
import { useRecoilState } from "recoil";
import {
  brokerConnectModalAtom,
  guideModalAtom,
} from "@/Atoms/modal/DialogueAtoms";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { useUserContext } from "@/contexts/userContext";
import { useToastHelper } from "@/helpers/helperFunctions";
import { useTranslation } from "react-i18next";
import {
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Tabs, TabsContent } from "../ui/tabs";

import { Input } from "../ui/input";
import { Button } from "../ui/button";
import Link from "next/link";
import Loader from "../Loader";
import {
  useGetCtraderAccounts,
  useGetFennelAccounts,
  useGetPublicUrl,
  usePostRevokeBrokerKeys,
  usePostSubmitBrokerKeys,
} from "@/api/brokerApi";
import { BROKER_LOGO } from "@/config/logos";
import TrafficSignalPill from "./TrafficSignalPill";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Label } from "../ui/label";
import { brokersList } from "../blocks/connection/BrokerList";
import { cn, formatCurrency } from "@/lib/utils";
import { Separator } from "../ui/separator";
import { FaCheckCircle, FaSearch } from "react-icons/fa";
import { useRouter } from "next/router";
import RevokeModal from "./RevokeModal";
import { useAppDetails } from "@/contexts/AppDetailsContext";

interface IFormValues {
  apiKey?: string;
  apiSecret?: string;
  passphrase?: string;
}

function BrokerConnectModal() {
  const connectForm = useForm<IFormValues>({
    mode: "onSubmit",
  });
  const [showConnectForm, setShowConnectForm] = useRecoilState(
    brokerConnectModalAtom
  );
  const [revokeOpen, setRevokeOpen] = useState<boolean>(false);
  const {
    user: userInfo,
    connectedBrokers,
    refreshConnectedBrokers,
  } = useUserContext();

  const [SCTab, setSCTab] = useState<any>(
    showConnectForm?.brokerId ?? Object.keys(connectedBrokers).length == 1
      ? Object.keys(connectedBrokers)?.[0].toString()
      : ""
  );
  const router = useRouter();
  const { details } = useAppDetails();
  const { toast } = useToastHelper();
  const { t } = useTranslation("broker-page");

  const {
    mutateAsync: revokeBrokerKeys,
    isPending: isRevokeLoading,
    isSuccess: isRevokeSuccess,
    isError: isRevokeError,
    error: revokeError,
  } = usePostRevokeBrokerKeys();
  const {
    mutateAsync: submitBrokerKeys,
    isPending: isSubmitLoading,
    data: submitData,
    isSuccess: isSubmitSuccess,
  } = usePostSubmitBrokerKeys();

  const { mutateAsync: getCtraderAccounts, isPending: ctraderLoading } =
    useGetCtraderAccounts();

  const { mutateAsync: getFennelAccounts, isPending: fennelLoading } =
    useGetFennelAccounts();

  const { mutateAsync: getPublicUrl, isPending: publicUrlLoading } =
    useGetPublicUrl();

  const { brokerConnected } = useMemo(() => {
    const connectedBroker = connectedBrokers?.[SCTab];
    return {
      brokerConnected: !!connectedBroker,
      connectedBroker: connectedBroker,
    };
  }, [connectedBrokers, SCTab]);

  const isSaving = isSubmitLoading || isRevokeLoading;
  const [showGuideModal, setShowGuideModal] = useRecoilState(guideModalAtom);
  const [accounts, setAccount] = useState<any[]>();
  const [fennelAccounts, setFennelAccount] = useState<any[]>();

  const [searchTerm, setSearchTerm] = useState("");

  const handleRevokeBrokerKeys = async () => {
    await revokeBrokerKeys({
      userId: userInfo?.user_id,
      key: String(SCTab),
    });
  };

  const handleCtraderConnect = async () => {
    try {
      const response = await getCtraderAccounts({
        access_token: connectForm.getValues("apiKey"),
      });
      setAccount(response?.data);
    } catch {}
  };

  const handleFennelConnect = async () => {
    try {
      const response = await getFennelAccounts({
        access_token: connectForm.getValues("apiSecret"),
      });
      setFennelAccount(response?.data?.accounts);
    } catch {}
  };

  const handleSubmitBrokerKeys: SubmitHandler<IFormValues> = async (
    values,
    event
  ) => {
    event?.preventDefault(); // Prevent form submission default behavior if applicable
    if (["19"].includes(SCTab)) {
      handlePublicConnect();
      return;
    }
    const { apiKey, apiSecret, passphrase } = values as Required<IFormValues>;
    try {
      await submitBrokerKeys({
        userId: userInfo?.user_id,
        key: brokersList.find((b) => b.id == SCTab)?.defaultApiKey ?? apiKey,
        secret:
          brokersList.find((b) => b.id == SCTab)?.defaultApiSecret ?? apiSecret,
        brokerId: SCTab,
        passphrase:
          brokersList.find((b) => b.id == SCTab)?.defaultPassphrase ??
          passphrase,
      });
    } catch (error: any) {
      toast({ title: error?.response?.data?.detail, variant: "destructive" });
    }
  };

  const handlePublicConnect = async () => {
    try {
      const res = await getPublicUrl({ user_id: userInfo?.user_id });
      router.push(res?.data?.url);
    } catch (error) {
      toast({ title: error?.response?.data?.detail, variant: "destructive" });
    }
  };

  const changeTab = (key: string) => {
    setSCTab(key);
  };

  useEffect(() => {
    connectForm.clearErrors();
    connectForm.reset();
  }, [SCTab]);

  useEffect(() => {
    if (isRevokeSuccess) {
      toast({ title: "Broker keys revoked successfully" });
      refreshConnectedBrokers();
      setShowConnectForm({ isOpen: false });
    } else if (isRevokeError && revokeError) {
      const errMessage = revokeError?.message;
      if (
        errMessage !==
        "Request free access to evaluate or upgrade to the Pro plan for full access."
      ) {
        toast({
          title: errMessage || t("errorWhileDisconnectingTheBroker"),
          variant: "destructive",
        });
      } else {
        toast({
          title: t("errorWhileDisconnectingTheBroker"),
          variant: "destructive",
        });
      }
    }
  }, [isRevokeLoading, isRevokeSuccess, isRevokeError]);

  useEffect(() => {
    if (isSubmitSuccess) {
      toast({
        title: submitData?.data?.message,
      });
      if (submitData?.data?.redirect_url) {
        router.push(submitData?.data?.redirect_url);
      }
      connectForm.setValue("apiKey", "");
      connectForm.setValue("apiSecret", "");
      connectForm.setValue("passphrase", "");
      refreshConnectedBrokers();
      setShowConnectForm({ isOpen: false });
    }
  }, [isSubmitLoading, isSubmitSuccess]);

  const renderInputField = (
    name: "passphrase" | "apiKey" | "apiSecret",
    label: string,
    placeholder: string,
    disabled = false,
    brokerConnectedString?: string
  ) => (
    <FormField
      control={connectForm.control}
      name={name}
      rules={{ required: !disabled }}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              autoComplete="off"
              disabled={disabled}
              placeholder={placeholder}
              {...field}
              value={
                disabled && brokerConnectedString
                  ? brokerConnectedString
                  : field.value || ""
              }
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <>
      <Dialog
        open={true}
        onOpenChange={() => {
          setShowConnectForm({ isOpen: false });
          connectForm.clearErrors();
          connectForm.reset();
        }}
      >
        <DialogContent className="max-w-5xl  py-0   sm:h-auto h-[100dvh]  overflow-y-scroll overflow-x-hidden px-8">
          <Form {...connectForm}>
            <form
              className="h-full xl:flex xl:flex-col w-full "
              method="post"
              onSubmit={
                brokerConnected
                  ? connectForm.handleSubmit(() => setRevokeOpen(true))
                  : connectForm.handleSubmit(handleSubmitBrokerKeys)
              }
              id="strategy-form"
            >
              <CardHeader className="px-0 w-full flex flex-col gap-0 ">
                <CardTitle className="flex  justify-between pr-4">
                  <div className="flex justify-between w-full">
                    <div className="text-2xl whitespace-nowrap font-bold tracking-tight ">
                      {t("Connect Broker Account")}
                    </div>
                    <div className="flex text-xs font-bold text-muted-foreground justify-between gap-0">
                      {" "}
                      <TrafficSignalPill
                        text={`${
                          Object.keys(connectedBrokers)?.length
                        } Connected`}
                        stoppedText="No Broker Connected"
                        className="text-muted-foreground font-bold"
                      ></TrafficSignalPill>
                    </div>
                  </div>
                </CardTitle>
              </CardHeader>

              <CardContent className="flex p-0 gap-8 min-h-[550px]">
                <div className="flex flex-col gap-4 mt-4 w-full sm:w-3/4 md:w-2/3 lg:w-1/2 mx-auto max-h-[550px] ">
                  {/* Header and Connection Selector */}
                  <div className="flex w-full items-center justify-between">
                    <Label className="text-sm font-medium text-muted-foreground">
                      Select Broker
                    </Label>
                  </div>

                  {/* Search Input */}
                  <div className="relative   px-2 pb-2 pr-2">
                    <Input
                      type="text"
                      placeholder="Search brokers..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full rounded-lg border border-gray-600 bg-transparent px-3 py-2 text-sm text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="absolute right-3 top-2.5 h-5 w-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z"
                      />
                    </svg>
                  </div>

                  {/* Broker List */}
                  <div className="grid grid-cols-3 gap-2  overflow-y-auto px-2 pb-2 pr-2 rounded-md scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent">
                    {brokersList
                      .filter((broker) =>
                        broker.name
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase())
                      )
                      .map((broker) => {
                        const isSelected = SCTab === broker.id;
                        const cBalance =
                          connectedBrokers?.[Number(broker.id)]?.balance;
                        const isDisabled = broker.disabled;

                        return (
                          <div
                            key={broker.id}
                            onClick={() => {
                              if (isDisabled) return;
                              setSCTab(broker.id);
                            }}
                            className={cn(
                              "cursor-pointer rounded-xl border p-5 h-[140px] overflow-hidden text-center transition-all flex flex-col gap-2 items-center justify-center relative shadow-sm",
                              isSelected
                                ? "border-blue-500 bg-blue-500/10"
                                : "border-gray-700 hover:border-gray-500 hover:bg-gray-800/40",
                              isDisabled && "opacity-50 hover:border-gray-700"
                            )}
                          >
                            {broker.logo && (
                              <img
                                src={broker.logo}
                                alt={broker.name}
                                className="mx-auto h-8  object-contain"
                              />
                            )}
                            <div>
                              <p className="text-xs text-muted-foreground">
                                {broker.note}
                              </p>
                              {cBalance && (
                                <div className="flex  bottom-0 justify-center w-full h-8 flex-col items-center gap-1">
                                  <div className="flex gap-1 items-center">
                                    <p
                                      className={cn(
                                        "font-semibold text-xs",
                                        cBalance > 0
                                          ? "text-[--marketplace-green]"
                                          : cBalance < 0
                                          ? "text-[--marketplace-red]"
                                          : "text-gray-300"
                                      )}
                                    >
                                      {formatCurrency(
                                        cBalance,
                                        2,
                                        false,
                                        undefined,
                                        true
                                      )}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>

                            {cBalance && (
                              <>
                                <div className="flex absolute bottom-0 justify-center  w-full h-6 bg-[--marketplace-green] items-center gap-1">
                                  <FaCheckCircle className="text-white text-xs" />
                                  <p className="text-xs font-bold">Connected</p>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>

                <Separator orientation="vertical"></Separator>
                <Tabs
                  onValueChange={(val) => {
                    changeTab(val);
                  }}
                  value={SCTab}
                  className=" w-1/2 "
                >
                  <TabsContent
                    value=""
                    className=" items-center justify-center flex"
                  >
                    <div className="flex flex-col items-center  justify-center py-16 text-center text-muted-foreground gap-3">
                      <FaSearch className="text-5xl text-muted" />
                      <p className="text-base font-medium">
                        Please select the broker you want to connect
                      </p>
                      <p className="text-base text-muted-foreground">
                        Don’t see your broker listed? Let us know and we’ll add
                        it
                      </p>
                    </div>
                  </TabsContent>

                  {/* For Alpaca (Virtual Trading) */}
                  <TabsContent value={"4"}>
                    <div className="flex flex-col gap-2  mt-0 px-4">
                      <div className=" flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.ALPACA.DARK.textLogo}
                          alt={"alpaca-logo"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />
                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("1. Open an Alpaca account")}
                          </CardTitle>
                          <CardDescription>
                            <a
                              href={"https://app.alpaca.markets/signup"}
                              className="underline"
                              target="_blank"
                            >
                              {t("Click here")}
                            </a>{" "}
                            {t(
                              "to open a free Virtual Trading account with Alpaca"
                            )}
                          </CardDescription>
                        </div>
                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("2. Connect to the API")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Enter your API credentials below to enable fully automated trading"
                            )}
                          </CardDescription>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        {renderInputField(
                          "apiKey",
                          t("API Key"),
                          t("API Key is Required"),
                          brokerConnected,
                          "********"
                        )}
                        {renderInputField(
                          "apiSecret",
                          t("Server"),
                          t(" API secret is required"),
                          brokerConnected,
                          "********"
                        )}
                      </div>
                    </div>
                  </TabsContent>

                  {/* For Ctrader (Virtual Trading) */}
                  <TabsContent value={"2"}>
                    <div className="flex flex-col gap-2  mt-0 px-4">
                      <div className=" flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.CTRADER.DARK.textLogo}
                          alt={"alpaca-logo"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />
                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("1.  Connect to the API")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Enter your API credentials below to enable fully automated trading"
                            )}
                          </CardDescription>
                        </div>
                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("2. Select the account you want to connect")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Choose the broker account you’d like to link to Level2"
                            )}
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <div className="flex items-end justify-between gap-2">
                          <div className="flex-1">
                            {renderInputField(
                              "apiKey",
                              t("Access Token"),
                              t("Acces Token is required"),
                              brokerConnected,
                              "*****"
                            )}
                          </div>
                          <Button
                            disabled={brokerConnected}
                            onClick={(e) => {
                              e.preventDefault();
                              handleCtraderConnect();
                            }}
                            loading={ctraderLoading}
                          >
                            Connect
                          </Button>
                        </div>
                        <FormField
                          control={connectForm.control}
                          name="apiSecret"
                          rules={{
                            required:
                              !brokerConnected && t("Please select one option"),
                          }}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{"Select Account"}</FormLabel>

                              <FormControl>
                                <Select
                                  disabled={!accounts?.length} // ✅ Disable Select if accounts is empty
                                  onValueChange={field.onChange}
                                  value={field.value}
                                >
                                  <SelectTrigger
                                    disabled={!accounts?.length} // ✅ Disable trigger as well
                                    className="w-full"
                                  >
                                    <SelectValue
                                      placeholder={t("Select Account")}
                                    />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectGroup>
                                      {accounts?.map(
                                        (
                                          account: {
                                            account_id: string;
                                            balance: string;
                                          },
                                          index: number
                                        ) => (
                                          <SelectItem
                                            value={account.account_id.toString()}
                                            key={`account-${index}`}
                                          >
                                            {account.account_id}{" "}
                                            {`($${account.balance})`}
                                          </SelectItem>
                                        )
                                      )}
                                    </SelectGroup>
                                  </SelectContent>
                                </Select>
                              </FormControl>
                              <FormMessage className="text-xs" />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </TabsContent>

                  {/* For Lightspeed (Virtual Trading) */}
                  <TabsContent value={"13"}>
                    <div className="flex flex-col gap-6 pt-4 px-4">
                      <div className="flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.LIGHTSPEED.DARK.icon}
                          alt={"lightspeed-logo"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />
                        {/* 4 */}
                        <div>
                          <CardDescription>
                            {t(
                              "Please note: This process uses single sign-on with your"
                            )}
                            <Link
                              className="text-[--accent-secondary] underline"
                              href={
                                "https://lightspeed.com/demo-request/lightspeed-web-mobile"
                              }
                              target="_blank"
                            >
                              {" "}
                              Lightspeed Web & Mobile
                            </Link>
                            {t(
                              " account credentials for convenience. A Lightspeed Connect API key is not required and the Lightspeed Trader Pro desktop is currently not supported."
                            )}
                          </CardDescription>
                        </div>{" "}
                        {/* 1 */}
                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("1. Click Connect")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Click the Connect button below to begin linking your Lightspeed Web & Mobile account."
                            )}
                          </CardDescription>
                        </div>
                        {/* 2 */}
                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("2. Sign in to Lightspeed")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "You’ll be redirected to the Lightspeed login page to securely sign in and authorize access."
                            )}
                          </CardDescription>
                        </div>
                        {/* 3 */}
                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("3. Complete the connection")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Once you’ve logged in, you’ll be automatically returned to Level2, where your Lightspeed account will be securely connected."
                            )}
                          </CardDescription>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* For Fennel (Virtual Trading) */}
                  <TabsContent value={"17"}>
                    <div className="flex flex-col gap-6 pt-4 px-4">
                      <div className=" flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.FENNEL.DARK.textLogo}
                          alt={"alpaca-logo"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />

                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("1. Open a Fennel account")}
                          </CardTitle>
                          <CardDescription>
                            <Link
                              target="_blank"
                              href={"https://fennel.com/"}
                              className="underline"
                            >
                              {t("Click here")}
                            </Link>{" "}
                            {t("to open a free Trading account.")}
                          </CardDescription>
                        </div>
                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("2. Subscribe for access")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Choose your subscription plan and subscribe for API access"
                            )}
                          </CardDescription>
                        </div>
                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("3. Connect to the API")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Enter your Fennel Personal Access Token and then select your preferred trading account to establish a connection between Level2 and Fennel"
                            )}
                          </CardDescription>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        <div className="flex items-end justify-between gap-2">
                          <div className="flex-1">
                            {renderInputField(
                              "apiSecret",
                              t("Fennel Token"),
                              t("Fennel Token is required"),
                              brokerConnected,
                              "********"
                            )}
                          </div>
                          <Button
                            disabled={brokerConnected}
                            onClick={(e) => {
                              e.preventDefault();
                              handleFennelConnect();
                            }}
                            loading={fennelLoading}
                          >
                            Connect
                          </Button>
                        </div>
                        <FormField
                          control={connectForm.control}
                          name="apiKey"
                          rules={{
                            required:
                              !brokerConnected && t("Please select one option"),
                          }}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{"Select Account"}</FormLabel>

                              <FormControl>
                                <Select
                                  disabled={!fennelAccounts?.length} // ✅ Disable Select if accounts is empty
                                  onValueChange={field.onChange}
                                  value={field.value}
                                >
                                  <SelectTrigger
                                    disabled={!fennelAccounts?.length} // ✅ Disable trigger as well
                                    className="w-full"
                                  >
                                    <SelectValue
                                      placeholder={t("Select Account")}
                                    />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectGroup>
                                      {fennelAccounts?.map(
                                        (
                                          account: {
                                            id: string;
                                            name: string;
                                          },
                                          index: number
                                        ) => (
                                          <SelectItem
                                            value={account.id.toString()}
                                            key={`account-${index}`}
                                          >
                                            {account.name}{" "}
                                          </SelectItem>
                                        )
                                      )}
                                    </SelectGroup>
                                  </SelectContent>
                                </Select>
                              </FormControl>
                              <FormMessage className="text-xs" />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </TabsContent>

                  {/* For Match-trade (Virtual Trading) */}
                  <TabsContent value={"1"}>
                    <div className="flex flex-col gap-6 pt-4 px-4">
                      <div className=" flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.MATCHTRADE.DARK.textLogo}
                          alt={"alpaca-logo"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />
                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("1. Open a MatchTrader account")}
                          </CardTitle>
                          <CardDescription>
                            <Link
                              target="_blank"
                              className="underline"
                              href={
                                "https://match-trader.com/download/#try-demo"
                              }
                            >
                              {t("Click here")}
                            </Link>{" "}
                            {t("to open a free Virtual Trading account")}
                          </CardDescription>
                        </div>
                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("2. Connect to the API")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Enter your API credentials below to enable fully automated trading"
                            )}
                          </CardDescription>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        {renderInputField(
                          "apiKey",
                          t("Match-Trader Registered Email"),
                          t("Match-Trader Registered Email"),
                          brokerConnected,
                          "********"
                        )}
                        {renderInputField(
                          "passphrase",
                          t("Match-Trader Password"),
                          t("Match-Trader Password"),
                          brokerConnected,
                          "********"
                        )}
                      </div>
                    </div>
                  </TabsContent>

                  {/* For Tradezero (Virtual Trading) */}
                  <TabsContent value={"5"}>
                    <div className="flex flex-col gap-6 pt-4 px-4">
                      <div className=" flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.TRADEZERO.DARK.icon}
                          alt={"alpaca-logo"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />

                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("1. Click Connect to begin")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Click the Connect button below to begin linking your TradeZero account"
                            )}
                          </CardDescription>
                        </div>

                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("2. Sign in to TradeZero")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "You’ll be redirected to the TradeZero login page to securely sign in and authorize access"
                            )}
                          </CardDescription>
                        </div>
                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("3. Complete the connection")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Once you’ve logged in, you’ll automatically return to Level2, where your TradeZero account will be securely connected"
                            )}
                          </CardDescription>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* For Trade station  */}
                  <TabsContent value={"18"}>
                    <div className="flex flex-col gap-6 pt-4 px-4">
                      <div className=" flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.TRADESTATION.DARK.textLogo}
                          alt={"Trade Station"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />

                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("1. Click Connect to begin")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Click the Connect button below to begin linking your Trade Station account"
                            )}
                          </CardDescription>
                        </div>

                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("2. Sign in to Trade Station")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "You’ll be redirected to the Trade Station login page to securely sign in and authorize access"
                            )}
                          </CardDescription>
                        </div>
                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("3. Complete the connection")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Once you’ve logged in, you’ll automatically return to Level2, where your Trade Station account will be securely connected"
                            )}
                          </CardDescription>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* For Trade station  */}
                  <TabsContent value={"19"}>
                    <div className="flex flex-col gap-6 pt-4 px-4">
                      <div className=" flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.PUBLIC.DARK.textLogo}
                          alt={"Public"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />

                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("1. Click Connect to begin")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Click the Connect button below to begin linking your Public account"
                            )}
                          </CardDescription>
                        </div>

                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("2. Sign in to Public")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "You’ll be redirected to the Public login page to securely sign in and authorize access"
                            )}
                          </CardDescription>
                        </div>
                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("3. Complete the connection")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Once you’ve logged in, you’ll automatically return to Level2, where your Public account will be securely connected"
                            )}
                          </CardDescription>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* For Leverate  */}
                  <TabsContent value={"11"}>
                    <div className="flex flex-col gap-2  mt-0 px-4">
                      <div className=" flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.LEVERATE.DARK.textLogo}
                          alt={"alpaca-logo"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />
                      </div>
                      <div>
                        <CardTitle className="text-base text-muted-foreground">
                          {t("1. Open a Leverate account ")}
                        </CardTitle>
                        <CardDescription>
                          {t(
                            "Contact the Leverate team directly to open a Trading account."
                          )}
                        </CardDescription>
                      </div>

                      <div className="">
                        <CardTitle className="text-base text-muted-foreground">
                          {t("2. Connect to the API")}
                        </CardTitle>
                        <CardDescription>
                          {t(
                            "Enter your API credentials below to enable fully automated trading"
                          )}
                        </CardDescription>
                      </div>
                      <div className="flex flex-col gap-2">
                        {renderInputField(
                          "apiKey",
                          t("Server ID "),
                          t("Server ID "),
                          brokerConnected,
                          "********"
                        )}
                        {renderInputField(
                          "apiSecret",
                          t("Account ID"),
                          t("Account ID "),
                          brokerConnected,
                          "********"
                        )}
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>

              {SCTab != "" && (
                <CardFooter className="grow px-0 gap-4 flex-col min-h-[105px]">
                  <div className="flex w-full h-full">
                    <div className="flex flex-col w-full float-right h-full">
                      <div className="flex flex-col w-full lg:flex-row justify-end">
                        {false ? (
                          <div className="flex w-full justify-center">
                            <Loader />
                          </div>
                        ) : (
                          <div className="w-full flex items-center justify-end">
                            <Button
                              disabled={isSaving}
                              loadingText={isSaving}
                              variant={
                                brokerConnected ? "destructive" : "default"
                              }
                              className={cn("text-sm gap-2 flex items-center")}
                            >
                              {isSaving ? (
                                <>
                                  {brokerConnected
                                    ? t("Revoking")
                                    : t("Connecting")}
                                </>
                              ) : (
                                <>
                                  {brokerConnected ? t("Revoke") : t("Connect")}
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                      </div>

                      {["4"].includes(SCTab) && (
                        <div className="text-xs text-muted-foreground flex gap-1 items-center">
                          {t("Not sure how to connect the API.")}{" "}
                          <Button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              if (SCTab == "1")
                                window.open(
                                  "https://learn.trylevel2.com/docs/technical/trading-with-match-trade",
                                  "_blank"
                                );
                              else if (SCTab == "2") {
                                window.open(
                                  "https://learn.trylevel2.com/docs/technical/getting-started-with-cTrader"
                                );
                              } else
                                setShowGuideModal({ isOpen: true, link: "" });
                            }}
                            variant="link"
                            className="p-0 text-xs text-[var(--accent-primary)]"
                          >
                            {t("Click here")}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardFooter>
              )}
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <RevokeModal
        isOpen={revokeOpen}
        onClose={() => setRevokeOpen(false)}
        onConfirm={() => handleRevokeBrokerKeys()}
      ></RevokeModal>
    </>
  );
}

export default BrokerConnectModal;
